import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { routerNavigatedAction } from '@ngrx/router-store';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../core/models/base-state/base-state';
import { PatientReport } from '../../models/patient-report';
import { ReportsListPosition } from '../../models/reports-list-position';
import * as actions from '../actions/reports.actions';

export const reportsFeatureKey = 'reports';

export interface ReportsState extends EntityState<PatientReport> {
  baseState: baseState.BaseState;
  // Scroll/filter memory for returning from the mobile report viewer (see rememberListPosition)
  listPosition: ReportsListPosition | null;
}

export const reportsAdapter = createEntityAdapter<PatientReport>({
  selectId: report => report.reportId,
});

export const initialState: ReportsState = reportsAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  listPosition: null,
});

export const reportsReducer = createReducer<ReportsState>(
  initialState,
  on(actions.getReports, (state): ReportsState => {
    return {
      ...reportsAdapter.removeAll(state),
      // A fresh load invalidates any remembered list position.
      listPosition: null,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(actions.getReports, (state): ReportsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: 1,
        pageSize: 10,
      },
    };
  }),

  on(actions.getReportsSuccess, (state, { reports }): ReportsState => {
    if (reports !== null) return reportsAdapter.addMany(reports, state);
    return state;
  }),

  on(actions.getReportsSuccess, (state): ReportsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    };
  }),

  on(actions.getReportsFailure, (state, { error }): ReportsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  // Retry of a failed page: clear the error and show loading, keeping pageNumber and every loaded
  // entity. Note it does NOT increment pageNumber the way nextPage below does — retrying with
  // nextPage would skip the page that failed.
  on(
    actions.retryPage,
    (state): ReportsState => ({
      ...state,
      baseState: { ...state.baseState, error: null, isLoading: true },
    })
  ),

  on(actions.nextPage, (state): ReportsState => {
    const currentPage = state.baseState.pageNumber ? state.baseState.pageNumber + 1 : 1;
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: currentPage,
      },
    };
  }),

  // The return from the viewer keeps these entities instead of refetching them, so the report just
  // opened is marked read here; otherwise it would show unread again on every later return.
  on(
    actions.rememberListPosition,
    (state, { position }): ReportsState => ({
      ...reportsAdapter.updateOne({ id: position.lastViewedReportId, changes: { isRead: true } }, state),
      listPosition: position,
    })
  ),

  on(actions.clearListPosition, (state): ReportsState => ({ ...state, listPosition: null })),

  // A position is only good for the trip back from the viewer. Any other destination (Messages, Home,
  // a deep link) must not leave it set for the next, unrelated visit to Reports to restore.
  on(routerNavigatedAction, (state, { payload }): ReportsState => {
    if (state.listPosition === null || isReportViewerUrl(payload.routerState.url)) {
      return state;
    }
    return { ...state, listPosition: null };
  })
);

function isReportViewerUrl(url: string): boolean {
  return /\/report-viewer\//.test(url);
}
